// ref: docs/CODEMAPS/PATTERNS.md §4.3 Hooks — Extracting UI Logic
import type { ICustomSelectProps } from '@/shared/ui';
import _ from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { t } from 'ttag';
import { showMessageError } from '@/shared/lib';
import type { ISearchSelectInput } from './index';

/**
 * Backs an asynchronous search select, owning the fetch state, option list and search text. Debounces the caller's fetchOptions by debounceTimeout, discards stale responses via a monotonically increasing fetch id and surfaces fetch errors as a message, optionally strips non-digits when isNumberOnly, and renders the loading, type-to-search or no-match empty state.
 *
 * @param props - Option fetcher plus the debounce timeout and number-only flag.
 * @returns The fetch flag, options, search value, search handler and empty-state renderer.
 */
export const useSearchSelectInput = (props: ISearchSelectInput) => {
  const { fetchOptions, debounceTimeout = 800, isNumberOnly = false } = props;

  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ICustomSelectProps['options']>([]);
  const [searchValue, setSearchValue] = useState('');
  const fetchRef = useRef(0);

  /**
   * Memoized debounced option loader for the search select, recreated when fetchOptions or debounceTimeout change. Wraps loadOptions with lodash debounce so rapid typing triggers a single fetch, tracking a fetch id to discard stale responses and surfacing errors via showMessageError.
   *
   * @returns The debounced option-loading function.
   */
  const debounceFetcher = useMemo(() => {
    /**
     * Fetches select options for a search term while guarding against out-of-order responses. Increments a fetch id, clears the current options, and on resolution ignores the result if a newer fetch has started, otherwise stores the options and clears the fetching flag, reporting failures through showMessageError.
     *
     * @param value - The search term to fetch options for.
     * @returns Nothing.
     */
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);

      fetchOptions(value)
        .then((newOptions) => {
          if (fetchId !== fetchRef.current) {
            return;
          }
          setOptions(newOptions);
          setFetching(false);
        })
        .catch((error) => {
          showMessageError({ content: error.message });
        });
    };

    return _.debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  /**
   * Handles a search input change on the asynchronous select. Strips non-digit characters when isNumberOnly and, only when the value actually changed, raises the fetching flag, stores the new search value and triggers the debounced fetcher.
   *
   * @param value - The new search input value.
   * @returns Nothing.
   */
  const onSearch = (value: string) => {
    let newSearchValue = value;

    if (isNumberOnly) {
      newSearchValue = _.replace(newSearchValue, /[^0-9]/g, '');
    }

    if (newSearchValue !== searchValue) {
      setFetching(true);
      setSearchValue(newSearchValue);
      debounceFetcher(newSearchValue);
    }
  };

  /**
   * Produces the search select dropdown's empty-state text. Returns a loading message while a fetch is in flight, a prompt to start typing when no search value is set, and a no-match message otherwise.
   *
   * @returns The localized empty-state message.
   */
  const renderNotFoundContent = () => {
    if (fetching) {
      return t`Loading...`;
    }

    if (!searchValue) {
      return t`Type to search...`;
    }

    return t`No option match`;
  };

  return { fetching, options, searchValue, onSearch, renderNotFoundContent };
};
